import * as React from 'react';
import { DeepReadonly } from 'utility-types';
import { ISubsidiariesOrganization, IUserOrganization } from '@pec/aion-ui-core/interfaces/userOrganization';
import { IOrganization } from '@pec/aion-ui-core/interfaces/organization';
import { SectionContainer } from '../../containers/Nav/Section';
import { UserOrganizationItem } from './UserOrganizationItem';
import { useTranslation } from 'react-i18next';
import CompanySwitcherIcon from './CompanySwitcherIcon';
import { makeStyles } from '@material-ui/core';

export type Props = {
  userOrganizations: DeepReadonly<IUserOrganization[]>;
  organization?: DeepReadonly<IOrganization> | null;
};

const useStyles = makeStyles(() => ({
  listItem: {
    '& svg': {
      fontSize: 24,
      margin: '0 4px'
    }
  },
  activeListItem: {
    '& span': {
      fontWeight: 700
    }
  }
}));

export const UserOrganizationSelector: React.FC<Props> = ({ userOrganizations, organization }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const renderChild = (userOrganization: ISubsidiariesOrganization) => {
    const subsidiaryOrganization = {
      id: userOrganization.organizationId,
      name: userOrganization.organizationName,
      features: userOrganization.features
    };
    const isCurrentOrganization = subsidiaryOrganization.id === organization?.id;
    return (
      <React.Fragment key={subsidiaryOrganization.id}>
        <div className={`${classes.listItem} ${isCurrentOrganization ? classes.activeListItem : undefined}`}>
          <UserOrganizationItem userOrganization={subsidiaryOrganization} rank={userOrganization.rank} />
        </div>
        {userOrganization.children.map((child: ISubsidiariesOrganization) => renderChild(child))}
      </React.Fragment>
    );
  };

  return (
    <SectionContainer
      id="OrganizationsSelector"
      subheader={t('components.userOrganizationSelector.switchCompany', 'Switch Company')}
      icon={<CompanySwitcherIcon />}
    >
      {userOrganizations.map(userOrganization => {
        const isCurrentOrganization = userOrganization.id === organization?.id;
        const { id, features, name, subsidiariesHierarchy } = userOrganization;
        return (
          <React.Fragment key={userOrganization.id}>
            <div className={`${classes.listItem} ${isCurrentOrganization ? classes.activeListItem : undefined}`}>
              <UserOrganizationItem userOrganization={{ id, features, name }} />
            </div>
            {subsidiariesHierarchy.children.map((org: ISubsidiariesOrganization) => renderChild(org))}
          </React.Fragment>
        );
      })}
    </SectionContainer>
  );
};
